<template>
  <div class="map-page-wrapper">
    <div v-if="isLoading" class="loading-overlay">
      <h1><i class="fas fa-spinner fa-spin"></i> Charting the World...</h1>
    </div>

    <div v-else-if="loadError" class="loading-overlay">
      <h1>
        <i class="fas fa-exclamation-triangle"></i>
        {{ loadError }}
      </h1>
    </div>

    <div v-else-if="!hasAnchor" class="loading-overlay">
      <h1><i class="fas fa-dungeon"></i> Found a city to appear on the World Map.</h1>
      <p class="empty-hint">
        Your empire is plotted from your first city. Return here after founding.
      </p>
    </div>

    <div
      v-else
      ref="viewportRef"
      class="world-viewport"
      :class="{ 'panel-open': panelOpen }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @wheel.prevent="onWheel"
    >
      <button
        type="button"
        class="recenter-btn"
        title="Recenter Map"
        @pointerdown.stop
        @click="recenter"
      >
        <i class="fas fa-crosshairs"></i>
      </button>

      <div
        class="world-legend"
        :class="{ 'panel-open': panelOpen }"
        @pointerdown.stop
      >
        <span
          v-for="(color, slug) in raceColors"
          :key="slug"
          class="legend-item"
        >
          <span class="legend-swatch" :style="{ background: color }"></span>
          {{ slug }}
        </span>
      </div>

      <div
        class="world-pan-zoom"
        :style="{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
        }"
      >
        <div
          class="world-iso"
          :style="{
            width: `${gridSize.width}px`,
            height: `${gridSize.height}px`,
          }"
        >
          <div
            v-for="hex in hexTiles"
            :key="hex.key"
            class="hex-tile"
            :class="{
              occupied: !!hex.empire,
              self: hex.empire?.isSelf,
              selected: isSelectedHex(hex),
              unknown: isUnknownHex(hex),
              hovered: hoveredHexKey === hex.key,
              shielded: isShieldedHex(hex),
            }"
            :style="hexStyle(hex)"
          >
            <div
              class="hex-face"
              :title="hex.empire?.cityName || hex.empire?.nickname || ''"
              @pointerdown="onHexPointerDown(hex)"
              @pointerenter="onHexPointerEnter(hex)"
              @pointerleave="onHexPointerLeave(hex)"
            ></div>
            <div v-if="isShieldedHex(hex)" class="hex-shield-dome" aria-hidden="true"></div>
            <div v-if="showHexLabel(hex)" class="hex-label">
              {{ hex.empire.cityName }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <WorldSideMenu v-if="panelOpen" />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useGameStore } from "../store/game";
import { useUiStore } from "../store/ui";
import { RACE_COLORS, getRaceColor } from "../utils/raceColors";
import WorldSideMenu from "../components/WorldSideMenu.vue";
import { isCityProtected } from "../utils/cityShield";

const HEX_SIZE = 42;
const HEX_WIDTH = Math.sqrt(3) * HEX_SIZE;
const HEX_HEIGHT = 2 * HEX_SIZE;
const MIN_RADIUS = 8;
const PAD_RADIUS = 3;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 2.2;
const CLICK_SLOP = 6;

const route = useRoute();
const gameStore = useGameStore();
const uiStore = useUiStore();
const viewportRef = ref(null);
const zoomLevel = ref(0.85);
const panOffset = ref({ x: 0, y: 0 });
const isPanning = ref(false);
const panStartPos = ref({ x: 0, y: 0 });
const pointerOrigin = ref({ x: 0, y: 0 });
const didPan = ref(false);
const pendingHex = ref(null);
const hoveredHexKey = ref(null);
const nowMs = ref(Date.now());
let shieldTimer = null;

const raceColors = RACE_COLORS;
const isLoading = computed(() => gameStore.isWorldMapLoading);
const loadError = computed(() => gameStore.worldMapError);
const empires = computed(() => gameStore.worldMap || []);
const hasAnchor = computed(() => empires.value.length > 0);
const panelOpen = computed(() => !!gameStore.selectedWorldEmpire);

function isSelectedHex(hex) {
  const selected = gameStore.selectedWorldEmpire;
  if (!hex?.empire || !selected) return false;
  return Number(hex.empire.id) === Number(selected.id);
}

function isUnknownHex(hex) {
  if (!hex?.empire || hex.empire.isSelf) return false;
  return String(hex.empire.race || "").toLowerCase() === "unknown";
}

function isShieldedHex(hex) {
  if (!hex?.empire) return false;
  return isCityProtected(
    hex.empire.protected_until,
    gameStore.clockOffsetMs,
    nowMs.value
  );
}

function showHexLabel(hex) {
  if (!hex?.empire) return false;
  if (hex.empire.isSelf) return true;
  if (isSelectedHex(hex)) return true;
  return hoveredHexKey.value === hex.key;
}

function onHexPointerEnter(hex) {
  if (hex?.empire && !hex.empire.isSelf) {
    hoveredHexKey.value = hex.key;
  }
}

function onHexPointerLeave(hex) {
  if (hoveredHexKey.value === hex.key) {
    hoveredHexKey.value = null;
  }
}

function applyEmpireQuery() {
  const raw = route.query.empire;
  if (raw == null || raw === "") return;
  const id = Number(raw);
  if (!Number.isFinite(id)) return;
  const empire = empires.value.find((entry) => Number(entry.id) === id);
  if (!empire) return;
  gameStore.selectWorldEmpire(empire);
  uiStore.setSidePanelOpen(true);
}

function selectHex(hex) {
  if (!hex?.empire) {
    gameStore.selectWorldEmpire(null);
    uiStore.setSidePanelOpen(false);
    return;
  }
  gameStore.selectWorldEmpire(hex.empire);
  uiStore.setSidePanelOpen(true);
}

function cubeDistance(q, r) {
  return (Math.abs(q) + Math.abs(r) + Math.abs(-q - r)) / 2;
}

function axialToPixel(q, r) {
  return {
    x: HEX_SIZE * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r),
    y: HEX_SIZE * (1.5 * r),
  };
}

function hexesInRadius(radius) {
  const cells = [];
  for (let q = -radius; q <= radius; q += 1) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r += 1) {
      cells.push({ q, r });
    }
  }
  return cells;
}

const occupiedByKey = computed(() => {
  const map = new Map();
  for (const empire of empires.value) {
    map.set(`${empire.pos_x},${empire.pos_y}`, empire);
  }
  return map;
});

const layout = computed(() => {
  let maxDist = 0;
  for (const empire of empires.value) {
    maxDist = Math.max(
      maxDist,
      cubeDistance(Number(empire.pos_x) || 0, Number(empire.pos_y) || 0)
    );
  }
  const radius = Math.max(MIN_RADIUS, Math.ceil(maxDist) + PAD_RADIUS);
  const cells = hexesInRadius(radius);
  const pixels = cells.map((cell) => ({
    ...cell,
    ...axialToPixel(cell.q, cell.r),
  }));
  const minX = Math.min(...pixels.map((p) => p.x));
  const minY = Math.min(...pixels.map((p) => p.y));
  const maxX = Math.max(...pixels.map((p) => p.x));
  const maxY = Math.max(...pixels.map((p) => p.y));
  const pad = HEX_SIZE;
  return {
    originX: minX - pad,
    originY: minY - pad,
    width: maxX - minX + HEX_WIDTH + pad * 2,
    height: maxY - minY + HEX_HEIGHT + pad * 2,
    radius,
  };
});

const gridSize = computed(() => ({
  width: layout.value.width,
  height: layout.value.height,
}));

const hexTiles = computed(() => {
  const { originX, originY, radius } = layout.value;
  const occupied = occupiedByKey.value;
  return hexesInRadius(radius).map((cell) => {
    const pixel = axialToPixel(cell.q, cell.r);
    return {
      key: `${cell.q},${cell.r}`,
      q: cell.q,
      r: cell.r,
      left: pixel.x - originX,
      top: pixel.y - originY,
      empire: occupied.get(`${cell.q},${cell.r}`) || null,
    };
  });
});

function hexStyle(hex) {
  const color = hex.empire ? getRaceColor(hex.empire.race) : "#2a3148";
  return {
    left: `${hex.left}px`,
    top: `${hex.top}px`,
    width: `${HEX_WIDTH}px`,
    height: `${HEX_HEIGHT}px`,
    "--hex-color": color,
  };
}

function tilePixel(q, r) {
  const pixel = axialToPixel(q, r);
  return {
    x: pixel.x - layout.value.originX + HEX_WIDTH / 2,
    y: pixel.y - layout.value.originY + HEX_HEIGHT / 2,
  };
}

function centerOnTarget() {
  const viewport = viewportRef.value;
  if (!viewport) return;
  const self = empires.value.find((empire) => empire.isSelf);
  const target = self
    ? { q: Number(self.pos_x) || 0, r: Number(self.pos_y) || 0 }
    : { q: 0, r: 0 };
  const pixel = tilePixel(target.q, target.r);
  const zoom = zoomLevel.value;
  panOffset.value = {
    x: viewport.clientWidth / 2 - pixel.x * zoom,
    y: viewport.clientHeight / 2 - pixel.y * zoom,
  };
}

function recenter() {
  zoomLevel.value = 0.85;
  centerOnTarget();
}

function onHexPointerDown(hex) {
  pendingHex.value = hex;
}

function onPointerDown(event) {
  if (event.button != null && event.button !== 0) return;
  if (event.target === event.currentTarget) {
    pendingHex.value = null;
  }
  didPan.value = false;
  pointerOrigin.value = { x: event.clientX, y: event.clientY };
  isPanning.value = true;
  panStartPos.value = {
    x: event.clientX - panOffset.value.x,
    y: event.clientY - panOffset.value.y,
  };
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function onPointerMove(event) {
  if (!isPanning.value) return;
  const dx = event.clientX - pointerOrigin.value.x;
  const dy = event.clientY - pointerOrigin.value.y;
  if (Math.hypot(dx, dy) > CLICK_SLOP) {
    didPan.value = true;
    hoveredHexKey.value = null;
  }
  panOffset.value = {
    x: event.clientX - panStartPos.value.x,
    y: event.clientY - panStartPos.value.y,
  };
}

function onPointerUp() {
  isPanning.value = false;
  if (!didPan.value && pendingHex.value) {
    selectHex(pendingHex.value);
  }
  pendingHex.value = null;
}

function onWheel(event) {
  const zoomFactor = 0.1;
  const direction = event.deltaY < 0 ? 1 : -1;
  const next = zoomLevel.value + zoomFactor * direction;
  if (next < MIN_ZOOM || next > MAX_ZOOM) return;

  const rect = event.currentTarget.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;
  const worldX = (mouseX - panOffset.value.x) / zoomLevel.value;
  const worldY = (mouseY - panOffset.value.y) / zoomLevel.value;
  panOffset.value = {
    x: mouseX - worldX * next,
    y: mouseY - worldY * next,
  };
  zoomLevel.value = next;
}

onMounted(async () => {
  shieldTimer = setInterval(() => {
    nowMs.value = Date.now();
  }, 1000);
  try {
    await gameStore.fetchWorldMap();
  } catch {
    /* overlay shows worldMapError */
  }
  try {
    await Promise.all([
      gameStore.fetchScoutingMissions(),
      gameStore.fetchWorldIntel(),
      gameStore.fetchPlayerResources(),
    ]);
  } catch {
    /* side menu can retry */
  }
  await nextTick();
  centerOnTarget();
  applyEmpireQuery();
});

onUnmounted(() => {
  if (shieldTimer) clearInterval(shieldTimer);
  gameStore.selectWorldEmpire(null);
  uiStore.setSidePanelOpen(false);
});

watch(
  () => empires.value,
  () => {
    applyEmpireQuery();
  }
);

watch(
  () => route.query.empire,
  () => {
    applyEmpireQuery();
  }
);
</script>

<style scoped>
.map-page-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background: transparent;
}

.loading-overlay {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  background-color: rgba(10, 10, 20, 0.7);
  backdrop-filter: blur(5px);
  text-align: center;
  padding: 1.5rem;
}

.empty-hint {
  max-width: 28rem;
  color: #a9b1d6;
  font-family: "Exo 2", sans-serif;
  font-size: 0.95rem;
}

.world-viewport {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: grab;
  perspective: 900px;
  touch-action: none;
  user-select: none;
}

.world-viewport:active {
  cursor: grabbing;
}

.world-viewport.panel-open {
  padding-right: 370px;
  box-sizing: border-box;
}

.world-pan-zoom {
  width: 1px;
  height: 1px;
  transform-origin: 0 0;
  will-change: transform;
}

.world-iso {
  position: relative;
  overflow: visible;
  transform: rotateX(60deg) rotateZ(-45deg);
  transform-style: preserve-3d;
}

.hex-tile {
  position: absolute;
  overflow: visible;
  transform-style: preserve-3d;
  pointer-events: none;
}

.hex-tile.occupied {
  z-index: 1;
}

.hex-tile.self {
  z-index: 2;
}

.hex-tile.hovered,
.hex-tile.selected {
  z-index: 3;
}

.hex-tile.shielded {
  z-index: 4;
}

.hex-face {
  width: 100%;
  height: 100%;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background: rgba(16, 20, 36, 0.55);
  border: 1px solid rgba(80, 90, 120, 0.35);
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.45);
  pointer-events: auto;
}

.hex-tile.occupied .hex-face {
  background:
    linear-gradient(rgba(10, 12, 24, 0.38), rgba(10, 12, 24, 0.38)),
    var(--hex-color);
  border-color: var(--hex-color);
  filter: drop-shadow(0 0 10px var(--hex-color));
}

.hex-tile.self .hex-face {
  filter:
    drop-shadow(0 0 16px var(--hex-color)) drop-shadow(0 0 6px #fff);
}

.hex-tile.selected .hex-face {
  filter:
    drop-shadow(0 0 18px #00f6ff) drop-shadow(0 0 8px var(--hex-color));
  border-color: #00f6ff;
}

.hex-shield-dome {
  position: absolute;
  left: 50%;
  top: 42%;
  width: 118%;
  height: 78%;
  transform: translate(-50%, -50%) rotateX(18deg);
  border-radius: 50%;
  border: 2px solid rgba(0, 240, 255, 0.7);
  box-shadow:
    0 0 14px rgba(0, 240, 255, 0.55),
    inset 0 0 18px rgba(0, 240, 255, 0.28);
  background: radial-gradient(
    ellipse at 50% 40%,
    rgba(0, 240, 255, 0.22),
    rgba(0, 240, 255, 0.05) 62%,
    transparent 70%
  );
  pointer-events: none;
  animation: shield-orbit 4.5s linear infinite;
}

@keyframes shield-orbit {
  0%,
  100% {
    box-shadow:
      0 0 10px rgba(0, 240, 255, 0.4),
      inset 0 0 12px rgba(0, 240, 255, 0.2);
  }
  50% {
    box-shadow:
      0 0 20px rgba(0, 240, 255, 0.75),
      inset 0 0 22px rgba(0, 240, 255, 0.38);
  }
}

.hex-label {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  transform: translate(-50%, -50%) rotateZ(45deg) rotateX(-60deg)
    translateY(-40px);
  color: #f4f7ff;
  font-family: "Exo 2", sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-shadow:
    0 0 8px rgba(0, 0, 0, 0.95),
    0 2px 6px rgba(0, 0, 0, 0.85),
    0 0 14px var(--hex-color);
  white-space: nowrap;
  overflow: visible;
  pointer-events: none;
}

.recenter-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  padding: 0;
  border-radius: 50%;
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
  background-color: rgba(36, 40, 59, 0.9);
  border: 2px solid #00f6ff;
  color: #00f6ff;
  cursor: pointer;
  transition: all 0.3s ease;
}

.recenter-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(0, 246, 255, 0.8);
}

.world-legend {
  position: absolute;
  top: 80px;
  right: 20px;
  transition: right 0.3s ease;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 0.9rem;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  background: rgba(10, 12, 24, 0.72);
  border: 1px solid rgba(0, 246, 255, 0.25);
  color: #c0caf5;
  font-family: "Exo 2", sans-serif;
  font-size: 0.75rem;
  text-transform: capitalize;
}

.world-legend.panel-open {
  right: 380px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 6px currentColor;
}

@media (max-width: 768px) {
  .world-viewport.panel-open {
    padding-right: 0;
  }

  .world-legend.panel-open {
    right: 20px;
  }
}
</style>
